import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import type { BoundingBox } from './types';

import { createThumbnailFromUrlCached } from './utils/thumbnailGenerator';

interface FaceThumbnailProps {
  assetUrl: string;
  boundingBox: BoundingBox;
  alt?: string;
  size?: number; // Square size in pixels, default 48px
}

interface ThumbnailContainerProps {
  $size: number;
}

const ThumbnailImage = styled.img<ThumbnailContainerProps>`
  width: ${({ $size }): number => $size}px;
  height: ${({ $size }): number => $size}px;
  border-radius: 4px;
  display: block;
  object-fit: cover;
`;

const PlaceholderContainer = styled.div<ThumbnailContainerProps>`
  width: ${({ $size }): number => $size}px;
  height: ${({ $size }): number => $size}px;
  border-radius: 4px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 12px;
`;

const LoadingContainer = styled.div<ThumbnailContainerProps>`
  width: ${({ $size }): number => $size}px;
  height: ${({ $size }): number => $size}px;
  border-radius: 4px;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpinnerIcon = styled.span`
  font-size: 16px;
`;

export const FaceThumbnail: React.FC<FaceThumbnailProps> = ({
  assetUrl,
  boundingBox,
  alt = '',
  size = 48
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const generateThumbnail = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(false);

        const url = await createThumbnailFromUrlCached(
          assetUrl,
          boundingBox,
          { size, quality: 0.9 }
        );

        if (isMounted) {
          setThumbnailUrl(url);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to generate face thumbnail:', err);
        if (isMounted) {
          setError(true);
          setIsLoading(false);
        }
      }
    };

    generateThumbnail();

    return () => {
      isMounted = false;
      // Note: Object URLs are cached and may be used by other components
      // Cache cleanup should be done at a higher level when appropriate
    };
  }, [assetUrl, boundingBox, size]);

  if (error) {
    return (
      <PlaceholderContainer $size={size} title={alt}>
        ?
      </PlaceholderContainer>
    );
  }

  if (isLoading || !thumbnailUrl) {
    return (
      <LoadingContainer $size={size} title={alt}>
        <SpinnerIcon className="icon bff-spinner" />
      </LoadingContainer>
    );
  }

  return (
    <ThumbnailImage
      $size={size}
      alt={alt}
      src={thumbnailUrl}
    />
  );
};
